"use client";

import React, { useEffect, useRef, useState } from "react";
import styles from "./TableOfContents.module.css";

// Set this flag to true to show debugging guides.
const showDebugGuides = false;

export interface Heading {
  title: string;
  slug: string;
}

export interface TableOfContentsProps {
  headings: Heading[];
  /**
   * The distance (in pixels) from the top of the viewport that is used as an offset.
   * Typically, this is the height of your fixed header.
   */
  topOffset?: number;
  /**
   * The distance (in pixels) from the bottom of the viewport to exclude from the observation window.
   * Default is 300px.
   */
  bottomOffset?: number;
}

/** Type used for drawing debug rectangles */
type DebugRect = {
  id: string;
  top: number;
  left: number;
  width: number;
  height: number;
  ratio: number;
};

const TableOfContents: React.FC<TableOfContentsProps> = ({
  headings,
  topOffset = 100,
  bottomOffset = 300,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [debugRects, setDebugRects] = useState<DebugRect[]>([]);

  // On initial mount, if a hash exists in the URL, scroll smoothly to that element.
  useEffect(() => {
    if (window.location.hash) {
      const slug = window.location.hash.substring(1);
      const element = document.getElementById(slug);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, []);

  // This function recalculates which elements (with data-tocslug) are visible,
  // computes their intersection ratios with the "observation area",
  // and then applies the selection algorithm.
  const recalcActiveEntry = () => {
    const elements = elementsRef.current;
    if (!elements.length) {
      setActiveId(null);
      return;
    }
    // Define the visible region (observation area) from topOffset to window.innerHeight - bottomOffset.
    const visibleTop = topOffset;
    const visibleBottom = window.innerHeight - bottomOffset;

    // Compute bounding rectangles and intersection ratios.
    const visibleEntries = elements
      .map((el) => {
        const rect = el.getBoundingClientRect();
        const intersectionHeight =
          Math.min(rect.bottom, visibleBottom) - Math.max(rect.top, visibleTop);
        const ratio = Math.max(0, intersectionHeight) / rect.height;
        return {
          el,
          ratio,
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        };
      })
      .filter((entry) => entry.ratio > 0);

    // Save these entries for debugging overlay.
    setDebugRects(
      visibleEntries.map((entry) => ({
        id: entry.el.dataset.tocslug || "",
        top: entry.top,
        left: entry.left,
        width: entry.width,
        height: entry.height,
        ratio: entry.ratio,
      })),
    );

    if (visibleEntries.length === 0) {
      setActiveId(null);
      return;
    }

    // Choose the element with the highest intersection ratio,
    // using the top position as a tie-breaker.
    const bestEntry = visibleEntries.reduce((prev, curr) => {
      if (curr.ratio > prev.ratio) return curr;
      if (curr.ratio === prev.ratio && curr.top < prev.top) return curr;
      return prev;
    });

    setActiveId(bestEntry.el.dataset.tocslug || null);
  };

  // Use an IntersectionObserver solely as a trigger to recalc which element is active.
  const elementsRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    // Query and cache the elements only once when the dependencies change.
    elementsRef.current = Array.from(
      document.querySelectorAll("[data-tocslug]"),
    ) as HTMLElement[];

    const observerOptions: IntersectionObserverInit = {
      root: null,
      rootMargin: `-${topOffset}px 0px -${bottomOffset}px 0px`,
      threshold: [0.5, 0.75, 0.9],
    };

    // The callback ignores its entries and simply recalculates everything.
    const observerCallback: IntersectionObserverCallback = () => {
      recalcActiveEntry();
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );
    elementsRef.current.forEach((el) => observer.observe(el));

    window.addEventListener("scroll", recalcActiveEntry);
    window.addEventListener("resize", recalcActiveEntry);
    // Do an initial calculation.
    recalcActiveEntry();
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", recalcActiveEntry);
      window.removeEventListener("resize", recalcActiveEntry);
    };
  }, [headings, topOffset, bottomOffset]);

  // Smooth scrolling on TOC link click.
  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    slug: string,
  ) => {
    e.preventDefault();
    const element = document.getElementById(slug);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      history.pushState(null, "", `#${slug}`);
    }
  };

  return (
    <>
      <nav className={styles.tocContainer} aria-label="Table of Contents">
        <ul className={styles.tocList}>
          {(headings || []).map(({ title, slug }) => (
            <li
              key={slug}
              className={`${styles.tocItem} ${
                activeId === slug ? styles.active : ""
              }`}
            >
              <a href={`#${slug}`} onClick={(e) => handleAnchorClick(e, slug)}>
                {title}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      {showDebugGuides && (
        <DebugOverlay
          topOffset={topOffset}
          bottomOffset={bottomOffset}
          debugRects={debugRects}
        />
      )}
    </>
  );
};

export interface ToCAnchorProps {
  /** The ID for the anchor so that links can target it */
  id?: string;
  /**
   * The distance (in pixels) from the top which determines the height and negative margin-top.
   * This should match your fixed header height.
   */
  top: number;
}

/**
 * The ToCAnchor component creates an invisible spacer so that headings
 * are positioned correctly beneath a fixed header.
 */
export const ToCAnchor: React.FC<ToCAnchorProps> = ({ id, top }) => {
  if (!id || id.trim() === "") return null;

  const style: React.CSSProperties = {
    display: "block",
    height: `${top}px`,
    marginTop: `-${top}px`,
    visibility: "hidden",
  };

  return <span id={id} style={style} />;
};

/* ===================== Debugging Code ===================== */
/**
 * DebugOverlay renders an overlay that shows:
 * - The observation area (in a red dashed rectangle).
 * - A rectangle around each element with a data-tocslug (all in green).
 */
const DebugOverlay: React.FC<{
  topOffset: number;
  bottomOffset: number;
  debugRects: DebugRect[];
}> = ({ topOffset, bottomOffset, debugRects }) => {
  // The observation area is from topOffset to window.innerHeight - bottomOffset.
  const observationHeight = window.innerHeight - topOffset - bottomOffset;
  const observationStyle: React.CSSProperties = {
    position: "fixed",
    top: topOffset,
    left: 0,
    width: "100%",
    height: observationHeight,
    border: "2px dashed red",
    pointerEvents: "none",
    zIndex: 9999,
  };

  return (
    <>
      <div style={observationStyle} />
      {debugRects.map((rect, index) => {
        const style: React.CSSProperties = {
          position: "fixed",
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          border: "2px solid green",
          pointerEvents: "none",
          zIndex: 10000,
        };
        return (
          <div key={index} style={style}>
            <div
              style={{
                background: "rgba(255,255,255,0.7)",
                fontSize: 12,
                padding: "0 2px",
              }}
            >
              {rect.id} ({Math.round(rect.ratio * 100)}%)
            </div>
          </div>
        );
      })}
    </>
  );
};

export default TableOfContents;
